import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { RSAHelper } from 'src/app/utils/rsa-helper';
import { AuthService } from '../@auth/auth.service';
import { HandleError } from '../utils/network-error-handler';

@Injectable()
export class AccountService {
    constructor(
        private _httpClient: HttpClient,
        private _apiService: ApiService,
        private _rsaHelper: RSAHelper,
        private _authService: AuthService,
    ) {
    }

    doUpdatePasswordAction(dto: { OldPassword: string, NewPassword: string, ConfirmPassword: string }) {
        dto.OldPassword = this._rsaHelper.encryptWithPublicKey(dto.OldPassword);
        dto.NewPassword = this._rsaHelper.encryptWithPublicKey(dto.NewPassword);
        dto.ConfirmPassword = this._rsaHelper.encryptWithPublicKey(dto.ConfirmPassword);

        return lastValueFrom(this._httpClient.post(this._apiService.endPoints.doUpdatePasswordAction_Account, dto)
        .pipe(catchError(error => throwError(error.error.Message || 'Une erreur est survenue lors de la requete au serveur'))));
    }

    doUpdatePincodeAction(dto: { OldPincode: string, NewPincode: string, ConfirmPincode: string }) {
        dto.OldPincode = this._rsaHelper.encryptWithPublicKey(dto.OldPincode);
        dto.NewPincode = this._rsaHelper.encryptWithPublicKey(dto.NewPincode);
        dto.ConfirmPincode = this._rsaHelper.encryptWithPublicKey(dto.ConfirmPincode);

        return lastValueFrom(this._httpClient.post(this._apiService.endPoints.doUpdateCodePinAction_Account, dto)
        .pipe(catchError(error => throwError(error.error.Message || 'Une erreur est survenue lors de la requete au serveur'))));
    }

    doRenewExpiredPassword(_newPassword: string, _confirmPassword: string): Promise<any> {
        return new Promise((resolve, reject) => {
            _newPassword = this._rsaHelper.encryptWithPublicKey(_newPassword);
            _confirmPassword = this._rsaHelper.encryptWithPublicKey(_confirmPassword);

            const authSession = this._authService.authenticatedSession$.value;
            if (!authSession) {
                reject('Session expirée');
                return;
            }

            this._httpClient.post(this._apiService.endPoints.doRenewExpiredPasswordAction_Account, { 
                NewPassword: _newPassword, 
                ConfirmPassword: _confirmPassword 
            }).subscribe(_response => {
                resolve(_response);
            }, (error) => {
                reject(!!error && !!error.error && !!error.error.Message ? error.error.Message : 'Une erreur est survenue lors de la requete au serveur');
            });
        });
    }

    doUpdateAccountInfosAction(dto: { NOM: string, PRENOM: string, EMAIL: string }) {
        return lastValueFrom(this._httpClient.post(this._apiService.endPoints.doUpdateAccountInfosAction_Account, dto)
        .pipe(catchError(error => throwError(!!error && !!error.error && !!error.error.Message ? error.error.Message : 'Une erreur est survenue lors de la requete au serveur'))));
    }

    doGenerateDoubleAuthenticationCodeAction() {
        return lastValueFrom(this._httpClient.post(this._apiService.endPoints.doGenerateDoubleAuthenticationCodeAction_Account, { })
        .pipe(catchError(error => throwError(!!error && !!error.error && !!error.error.Message ? error.error.Message : 'Une erreur est survenue lors de la requete au serveur'))));
    }

    doEnableDoubleAuthenticationAction(code: string) {
        return lastValueFrom(this._httpClient.post(this._apiService.endPoints.doEnableDoubleAuthenticationAction_Account, { MFA_CODE: code })
        .pipe(catchError(error => throwError(!!error && !!error.error && !!error.error.Message ? error.error.Message : 'Une erreur est survenue lors de la requete au serveur'))));
    }

    doDisableDoubleAuthenticationAction() {
        return lastValueFrom(this._httpClient.post(this._apiService.endPoints.doDisableDoubleAuthenticationAction_Account, { })
        .pipe(catchError(error => throwError(!!error && !!error.error && !!error.error.Message ? error.error.Message : 'Une erreur est survenue lors de la requete au serveur'))));
    }

    doGetDoubleAuthenticationCodeAction() {
        return lastValueFrom(this._httpClient.get<string>(this._apiService.endPoints.doGetDoubleAuthenticationCodeAction_Account, { responseType: 'string' as any })
        .pipe(catchError(error => lastValueFrom(HandleError(error)))));
    }

    doGetDoubleAuthenticationQRCodeAction() {
        return lastValueFrom(this._httpClient.get(this._apiService.endPoints.doGetDoubleAuthenticationQRCodeAction_Account, { responseType: 'blob' })
        .pipe(catchError(error => lastValueFrom(HandleError(error)))));
    }
}